// ============================================================================
// TRIAGE « À traiter » — SOURCE UNIQUE (décision utilisateur 2026-08-05) : les items (libellés,
// détails, sévérité, destination) sont définis ICI et consommés par le Dashboard ET la cloche de
// topbar — jamais deux listes qui divergent (§24.3bis). Chaque compteur est RÉEL ; une source en
// échec vaut `null` et est COMPTÉE comme indisponible, jamais convertie en zéro (§23.0).
// ============================================================================
import { api, type LegalSignal } from './api';
import { daysLeft } from './ui';
import { fmtDuration, mutedDevices, type DeviceContact } from './sync-health';

export interface TriageItem { label: string; detail: string; n: number; kind: 'da' | 'wa' | 'in'; to: string }

/** Compteurs bruts des sources (null = source indisponible). */
export interface TriageCounts {
  /** Les défaillances signalées par la veille (SA-9.3), en attente de décision — la liste, pour nommer la première. */
  legal: LegalSignal[] | null;
  lateReturns: number | null;
  /** Les appareils muets depuis plus de 24 h, du plus muet au moins muet (`mutedDevices` — le calcul de la Supervision). */
  muted: { store: string; device: DeviceContact }[] | null;
  locks: number | null;
  provisioning: number | null;
  dueSoon: number | null;
  /** Les locations (courtes ou longues) sans leurs deux dates : ni alerte d'échéance ni blocage automatique. */
  undatedRentals: number | null;
  draftsToReview: number | null;
  invited: number | null;
  migLate: number | null;
}

/** Les définitions, dans l'ordre de sévérité de la maquette — l'UNIQUE endroit où elles s'écrivent. */
export function buildTriageSources(c: TriageCounts): { n: number | null; item: (n: number) => TriageItem }[] {
  return [
    // ⚠️ EN TÊTE (SA-9.3) : un client en difficulté se lit avant tout le reste — et il ne se tranche qu'à la main.
    { n: c.legal === null ? null : c.legal.length, item: (n) => legalItem(c.legal!, n) },
    { n: c.lateReturns, item: (n) => ({ label: 'Retours de location en retard', detail: 'échéance dépassée — matériel à récupérer (lock auto à J+2)', n, kind: 'da', to: '/locations' }) },
    // ⚠️ DES APPAREILS, PAS DES MAGASINS, nommés du plus muet au moins muet — le même calcul que la Supervision, qui cite
    //    partout l'appareil le plus muet.
    { n: c.muted === null ? null : c.muted.length, item: (n) => ({ label: 'Appareils sans contact depuis plus de 24 h', detail: c.muted!.slice(0, 2).map((m) => `${m.store} ${m.device.code} · ${fmtDuration(m.device.hours)}`).join(' · '), n, kind: 'da', to: '/supervision' }) },
    { n: c.locks, item: (n) => ({ label: 'Blocages (lock) en cours', detail: "l'encaissement est coupé sur ces cibles — lever ou confirmer", n, kind: 'da', to: '/blocages' }) },
    { n: c.provisioning, item: (n) => ({ label: 'Magasins en mise en service', detail: 'base à provisionner (wizard → Mettre en service)', n, kind: 'wa', to: '/clients' }) },
    { n: c.dueSoon, item: (n) => ({ label: 'Locations à rendre sous 48 h', detail: 'relancer le client avant le blocage automatique', n, kind: 'wa', to: '/locations' }) },
    // ⚠️ UNE LOCATION NAÎT AVEC SES DEUX DATES (décision de l'utilisateur) ; celles affectées avant cette règle
    //    se signalent ici jusqu'à ce qu'on les complète — sans échéance, rien ne les suit.
    { n: c.undatedRentals, item: (n) => ({ label: 'Locations sans dates', detail: 'ni alerte d’échéance ni blocage automatique — à compléter dans « Régime et dates »', n, kind: 'wa', to: '/appareils' }) },
    { n: c.draftsToReview, item: (n) => ({ label: 'Brouillons à relire', detail: 'dossiers déposés en attente de validation humaine', n, kind: 'wa', to: '/clients?vue=brouillons' }) },
    { n: c.invited, item: (n) => ({ label: 'Invitations staff sans réponse', detail: 'comptes créés dans l’IdP, jamais activés — renvoyer ?', n, kind: 'wa', to: '/staff' }) },
    { n: c.migLate, item: (n) => ({ label: 'Bases en retard de migration', detail: 'schéma à rattraper — pré-vol puis Migrer', n, kind: 'in', to: '/migrations' }) },
  ];
}

/** Ce qui reste à traiter d'un relevé (les sources à zéro écartées), et combien de sources n'ont pas répondu. */
/**
 * La ligne « défaillance » : elle NOMME la plus récente (client, nature, date) comme la maquette, et mène à sa décision.
 * ⚠️ Le libellé dit la SOURCE : une cessation Sirene n'est pas une publication au BODACC.
 */
function legalItem(open: readonly LegalSignal[], n: number): TriageItem {
  const first = open[0];
  // Une liste vide fait une ligne à zéro, que la liste écarte ensuite — comme toutes les sources.
  if (!first) return { label: 'Défaillance signalée au BODACC', detail: '', n, kind: 'da', to: '/clients' };
  const nature = `${first.nature.charAt(0).toLowerCase()}${first.nature.slice(1)}`;
  const date = first.publishedOn ? ` publiée le ${first.publishedOn.slice(8, 10)}/${first.publishedOn.slice(5, 7)}` : '';
  const autres = n > 1 ? ` (et ${n - 1} autre${n > 2 ? 's' : ''})` : '';
  const bodacc = open.every((s) => s.source === 'bodacc');
  return {
    label: bodacc ? 'Défaillance signalée au BODACC' : 'Défaillance signalée',
    detail: `${first.tenantName} · ${nature}${date} — décision humaine requise${autres}`,
    n, kind: 'da', to: `/clients?defaillance=${first.id}`,
  };
}

export function triageItems(c: TriageCounts): { items: TriageItem[]; failed: number } {
  const sources = buildTriageSources(c);
  return {
    items: sources.filter((s) => s.n !== null).map((s) => s.item(s.n!)).filter((t) => t.n > 0),
    failed: sources.filter((s) => s.n === null).length,
  };
}

const SEVERITY: Record<TriageItem['kind'], number> = { da: 3, wa: 2, in: 1 };

/**
 * Le compteur de chaque entrée du menu : la somme de ce qui l'attend, au ton le plus grave.
 * ⚠️ DÉRIVÉ DE LA LISTE de la cloche, jamais relevé à part (décision du 12/10) : deux relevés finiraient par annoncer
 *    « 3 » dans le menu et « 2 » dans la cloche pour la même chose.
 */
export function triageByRoute(items: readonly TriageItem[]): Map<string, { n: number; kind: TriageItem['kind'] }> {
  const by = new Map<string, { n: number; kind: TriageItem['kind'] }>();
  for (const t of items) {
    // ⚠️ La PAGE, sans ce qui suit le « ? » : un élément qui ouvre un onglet précis (`/clients?vue=brouillons`) compte
    //    pour l'entrée « Clients » du menu.
    const page = t.to.split('?')[0]!;
    const cur = by.get(page);
    by.set(page, { n: (cur?.n ?? 0) + t.n, kind: cur && SEVERITY[cur.kind] >= SEVERITY[t.kind] ? cur.kind : t.kind });
  }
  return by;
}

/**
 * Échéances de location : en retard, et à rendre sous 48 h. ⚠️ EXPORTÉ — le Dashboard le réimplémentait
 * en ligne, si bien que la cloche et la carte « À traiter » pouvaient afficher deux nombres différents
 * pour la même flotte. Le comptage est délégué à `daysLeft` (jour civil, cf. ../ui).
 */
export function rentalBuckets(devices: Awaited<ReturnType<typeof api.devices>>['devices']): { late: number; dueSoon: number; undated: number } {
  const rentals = devices.filter((d) => d.status === 'assigned' && (d.rentalType === 'courte' || d.rentalType === 'longue'));
  const rows = rentals.filter((d) => d.rentalEnd);
  const k = (d: (typeof rows)[number]) => daysLeft(d.rentalEnd) ?? 0;
  return {
    late: rows.filter((d) => k(d) < 0).length,
    dueSoon: rows.filter((d) => k(d) >= 0 && k(d) <= 2).length,
    undated: rentals.filter((d) => !d.rentalStart || !d.rentalEnd).length,
  };
}

/** Relève les compteurs (chaque échec isolé → null, jamais un zéro inventé). Consommé par la cloche. */
export async function fetchTriageCounts(): Promise<TriageCounts> {
  const [legal, muted, blocks, devices, dash, drafts, staff, mig] = await Promise.all([
    api.legalWatch().then((r) => r.open).catch(() => null),
    // ⚠️ La tournée du parc est PARTAGÉE quelques minutes au serveur : la relire chaque minute ne la refait pas.
    api.supervision().then((r) => mutedDevices(r.stores)).catch(() => null),
    api.blocks().then((r) => r.active.filter((b) => b.level === 'lock').length).catch(() => null),
    api.devices().then((r) => rentalBuckets(r.devices)).catch(() => null),
    api.dashboard().then((r) => r.stores.provisioning).catch(() => null),
    api.drafts().then((r) => r.drafts.filter((x) => x.status === 'deposited').length).catch(() => null),
    api.staff().then((r) => r.staff.filter((x) => x.status === 'invited').length).catch(() => null),
    api.migrationsStatus().then((r) => r.stores.filter((x) => !x.upToDate).length).catch(() => null),
  ]);
  return {
    legal,
    muted,
    lateReturns: devices?.late ?? null,
    locks: blocks,
    provisioning: dash,
    dueSoon: devices?.dueSoon ?? null,
    undatedRentals: devices?.undated ?? null,
    draftsToReview: drafts,
    invited: staff,
    migLate: mig,
  };
}
